import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axios';
import { jwtDecode } from "jwt-decode";

const getInitialState = () => {
  try {
    // Corrected from localStorage.get to localStorage.getItem
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const user = JSON.parse(localStorage.getItem('user'));

    if (accessToken && user) {
      // If an access token and user info exist, we assume the user is authenticated.
      // A background check can verify the token's validity later.
      return {
        user,
        accessToken,
        refreshToken,
        isAuthenticated: true,
        loading: false,
        error: null
      };
    }
  } catch (error) {
    // If localStorage is corrupted or empty, return the default initial state.
    console.error("Could not load state from localStorage", error);
  }
  
  return {
    user: null,
    accessToken: null,
    refreshToken: null,
    isAuthenticated: false,
    loading: false,
    error: null
  };
};

// Helper function to verify token
const verifyToken = async () => {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) return false;

    // Try to make a request to verify the token
    await axiosInstance.get('/accounts/profile/');
    return true;
  } catch (error) {
    if (error.response?.status === 401) {
      // Try to refresh the token
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) return false;

        const response = await axiosInstance.post('/accounts/token/refresh/', {
          refresh: refreshToken
        });

        const { access } = response.data;
        localStorage.setItem('access_token', access);
        // Cookies.set('access_token', access, { expires: 1 });
        return true;
      } catch (refreshError) {
        // If refresh fails, clear tokens

        // Cookies.remove('access_token');
        // Cookies.remove('refresh_token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        return false;
      }
    }
    return false;
  }
};

// Create async thunks
export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/accounts/register/', userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Registration failed');
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/accounts/login/', credentials);
      const { access, refresh } = response.data;
      const decodedUser = jwtDecode(access);
    // Store tokens and user info in localStorage for persistence
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      localStorage.setItem('user', JSON.stringify(decodedUser));
      const {user_id, email, full_name, organization_id, organization_name, organization_role, is_superuser, is_staff} = jwtDecode(response.data.access);
      return {user_id, email, full_name, organization_id, organization_name, organization_role, is_superuser, is_staff};
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Login failed');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { getState, rejectWithValue }) => {
    const refreshToken = localStorage.getItem('refresh_token')
    try {
      if (refreshToken) {
        // Inform the backend that the refresh token is being invalidated
        await axiosInstance.post('/accounts/logout/', { refresh: refreshToken });
      }
    } catch (error) {
      // The logout should proceed on the client-side even if the server call fails.
      console.error("Server logout failed, proceeding with client-side cleanup.", error);
    } finally {
      // Always clear storage and state
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
    return { success: true };
  }
);

// Add a new thunk to check authentication status
export const checkAuth = createAsyncThunk(
  'auth/check',
  async (_, { rejectWithValue }) => {
    try {
      const isValid = await verifyToken();
      if (!isValid) {
        throw new Error('Invalid token');
      }
      return { isAuthenticated: true };
    } catch (error) {
      return rejectWithValue('Not authenticated');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: getInitialState(),
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetLoading: (state) => {
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      //Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAuth.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = true;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
      });
  }
});

export const { clearError, resetLoading } = authSlice.actions;
export default authSlice.reducer; 